/**
 * OptiBay Quality Command Center — ADMIN-ONLY, READ-ONLY QA evidence intelligence.
 *
 * Renders the REAL QE Platform V2 evidence ingested into the RLS-protected
 * platform_qa_* tables. Route-gated by <InternalAdminRoute>; this page ALSO
 * re-checks useIsInternalAdmin() so a direct mount without the guard still fails
 * closed. All data is read-only — QE Platform artifacts remain the source of truth.
 *
 * Honesty invariants: 8 canonical result classes rendered distinctly (text+icon);
 * missing/empty/malformed evidence degrades to NOT_EXECUTED/UNKNOWN/ARTIFACT ERROR,
 * never PASS; smoke/small-scale scope is always shown; trust posture is explicit.
 */
import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ShieldCheck, ArrowLeft, Activity, FlaskConical, GitCommit, AlertTriangle, ExternalLink, XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import {
  useQaReadiness, useQaEngines, useQaRuns, useQaFindings, useQaAuditDebt, useQaMutations, useQaCoverage, useQaWorkerHeartbeat,
} from '@/hooks/useQualityCommandCenter';
import { READINESS_LEVELS, evaluateLevel, normResult, collapseAttempts, attemptCounts, coverageRowStatus, type QaResult } from '@/lib/qa/commandCenterLogic';
import { ResultBadge, Loading, ErrorState, Empty, ageLabel } from './quality/shared';
import { PerformanceTab, SoakTab, SecurityMatrixTab, SimulationTab, ArtifactHealthTab, TrendsTab } from './quality/tabs';

const RESULT_FILTER_OPTIONS = ['ALL', 'PASS', 'FAIL_PRODUCT', 'FAIL_HARNESS', 'BLOCKED_ENV', 'BLOCKED_PRODUCT', 'NOT_EXECUTED', 'UNKNOWN', 'ARTIFACT_ERROR'];

function Select({ value, onChange, options, label }: { value: string; onChange: (v: string) => void; options: string[]; label: string }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} className="h-8 rounded-md border bg-background px-2 text-sm">
      {options.map((o) => <option key={o} value={o}>{o === 'ALL' ? `${label}: all` : o}</option>)}
    </select>
  );
}

/* ------------------------------------------------------------------ Overview */

function OverviewTab() {
  const readiness = useQaReadiness();
  const engines = useQaEngines();
  const findings = useQaFindings();
  const audit = useQaAuditDebt();

  if (readiness.isLoading || engines.isLoading) return <Loading />;
  if (readiness.error) return <ErrorState e={readiness.error} />;

  const snap = readiness.data;
  const age = ageLabel(snap?.captured_at);
  const counts = snap?.status_counts ?? {};
  const openFindings = (findings.data ?? []).filter((f) => !/FIXED|VERIFIED|CLOSED/i.test(f.status ?? ''));

  return (
    <div className="space-y-6">
      {snap ? (
        <Alert className="border-emerald-300 bg-emerald-50">
          <ShieldCheck className="h-4 w-4 text-emerald-700" />
          <AlertTitle className="text-emerald-900">Readiness recommendation: {snap.recommendation}</AlertTitle>
          <AlertDescription className="text-emerald-900/80">
            Derived from the authoritative launch-readiness scorecard. Counts — {Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(', ') || 'n/a'}.
          </AlertDescription>
        </Alert>
      ) : <Empty what="readiness" />}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><GitCommit className="h-4 w-4" /> Evidence provenance</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-1">
          <div>Source fingerprint: <code className="text-foreground">{snap?.source_fingerprint ?? 'unknown'}</code> · Environment: <code className="text-foreground">{snap?.environment ?? 'unknown'}</code></div>
          <div>Captured: {snap?.captured_at ? new Date(snap.captured_at).toLocaleString() : 'unknown'} ({age.text})
            {age.stale && <Badge variant="outline" className="ml-2 border-amber-400 text-amber-700">STALE — re-run before relying</Badge>}
          </div>
          <div className="text-xs pt-1">A browser SPA cannot verify its own build fingerprint at runtime, so build-match is shown as recorded at ingest — confirm the tested fingerprint against the deployed build manually.</div>
        </CardContent>
      </Card>

      <div>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><FlaskConical className="h-4 w-4" /> Engines ({engines.data?.length ?? 0}/8)</h3>
        {engines.error ? <ErrorState e={engines.error} /> : (engines.data?.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {engines.data.map((e) => {
              const r = normResult(e.result);
              return (
                <Card key={e.engine} className="overflow-hidden">
                  <CardHeader className="pb-2"><CardTitle className="text-sm capitalize">{e.engine}</CardTitle><ResultBadge result={r} /></CardHeader>
                  <CardContent className="text-xs text-muted-foreground space-y-0.5">
                    <div>Pass: <span className="text-foreground font-medium">{e.pass_count}</span> · Not executed: <span className="text-foreground font-medium">{e.not_executed}</span></div>
                    {(e.fail_product > 0 || e.fail_harness > 0) && <div className="text-red-600">Fail — product {e.fail_product} / harness {e.fail_harness}</div>}
                    {(e.blocked_env > 0 || e.blocked_product > 0) && <div className="text-amber-700">Blocked — env {e.blocked_env} / product {e.blocked_product}</div>}
                    {e.not_executed > 0 && <div className="text-slate-500">Scope gaps present — PASS does not cover not-executed paths.</div>}
                    <div>Residue: {e.residue ?? 'n/a'} · Seed: {e.seed || 'n/a'}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : <Empty what="engine" />)}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card><CardHeader className="pb-1"><CardDescription>Open findings</CardDescription><CardTitle className="text-2xl">{openFindings.length}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">of {findings.data?.length ?? 0} total ingested</CardContent></Card>
        <Card><CardHeader className="pb-1"><CardDescription>Audit debt (deferred scope)</CardDescription><CardTitle className="text-2xl">{audit.data?.length ?? 0}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">items blocking External Pilot / GA</CardContent></Card>
        <Card><CardHeader className="pb-1"><CardDescription>Engines with scope gaps</CardDescription><CardTitle className="text-2xl">{(engines.data ?? []).filter((e) => e.not_executed > 0).length}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">have NOT_EXECUTED paths</CardContent></Card>
      </div>

      <Alert className="border-slate-300">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Trust posture</AlertTitle>
        <AlertDescription className="text-sm">
          Suitable as a <strong>gating aid for Internal Pilot</strong>. <strong>Not sufficient alone</strong> for External Pilot or GA:
          several engines run at smoke / small-scale depth (24h soak and 10k-shop scale are NOT_EXECUTED), and simulation/soak/chaos
          are smoke-fidelity only. Human review remains required for production-readiness decisions.
        </AlertDescription>
      </Alert>
    </div>
  );
}

/* ------------------------------------------------------------------ Readiness matrix */

function ReadinessMatrixTab() {
  const readiness = useQaReadiness();
  if (readiness.isLoading) return <Loading />;
  if (readiness.error) return <ErrorState e={readiness.error} />;
  const cats = (readiness.data?.categories ?? {}) as Record<string, QaResult>;
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Each level requires ALL its categories at PASS. A missing category counts as NOT_EXECUTED — a level is never shown READY on absent evidence.</p>
      <div className="grid gap-3 md:grid-cols-2">
        {READINESS_LEVELS.map(({ level, required }) => {
          const ev = evaluateLevel(required, cats);
          return (
            <Card key={level}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  {level}
                  {required.length === 0
                    ? <Badge variant="outline" className="border-slate-300 text-slate-600">baseline</Badge>
                    : ev.ready
                      ? <Badge variant="outline" className="border-emerald-300 text-emerald-700">READY ({ev.passed}/{ev.required})</Badge>
                      : <Badge variant="outline" className="border-amber-400 text-amber-700">NOT READY ({ev.passed}/{ev.required})</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground">
                {required.length === 0 ? 'No gating categories.' : ev.ready ? 'All required categories pass.' : (
                  <div>Blocking: {ev.missing.slice(0, 8).map((m) => <Badge key={m} variant="outline" className="mr-1 mb-1 border-slate-300">{m}</Badge>)}{ev.missing.length > 8 && `+${ev.missing.length - 8} more`}</div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Runs (filter + search + link) */

/* ------------------------------------------------------------------ Coverage */

function CovBadge({ status, openFinding }: { status: string; openFinding: boolean }) {
  const cls = status === 'NEVER_TESTED' ? 'border-slate-400 text-slate-600'
    : status === 'STALE' ? 'border-amber-400 text-amber-700'
    : 'border-emerald-400 text-emerald-700';
  return (
    <span className="inline-flex gap-1">
      <Badge variant="outline" className={cls}>{status.replace('_', ' ')}</Badge>
      {openFinding && <Badge variant="outline" className="border-red-400 text-red-700">OPEN FINDING</Badge>}
    </span>
  );
}

function CoverageTab() {
  const coverage = useQaCoverage();
  const [persona, setPersona] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const rows = useMemo(() => (coverage.data ?? []).map((r) => ({ ...r, _status: coverageRowStatus(r) })), [coverage.data]);
  const personas = useMemo(() => ['ALL', ...Array.from(new Set(rows.map((r) => r.persona)))], [rows]);
  const filtered = rows.filter((r) => (persona === 'ALL' || r.persona === persona) && (status === 'ALL' || r._status === status));
  const summary = useMemo(() => {
    const total = rows.length; const tested = rows.filter((r) => r._status !== 'NEVER_TESTED').length;
    const stale = rows.filter((r) => r._status === 'STALE').length; const open = rows.filter((r) => (r.open_finding_count ?? 0) > 0).length;
    return { total, tested, never: total - tested, stale, open };
  }, [rows]);

  if (coverage.isLoading) return <Loading />;
  if (coverage.error) return <ErrorState e={coverage.error} />;
  if (!rows.length) return <Empty what="coverage combination" />;

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-4">
        <Card><CardHeader className="pb-1"><CardDescription>Combinations tracked</CardDescription><CardTitle className="text-2xl">{summary.total}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">persona × charter × behavior</CardContent></Card>
        <Card><CardHeader className="pb-1"><CardDescription>Never tested</CardDescription><CardTitle className="text-2xl">{summary.never}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">coverage gaps</CardContent></Card>
        <Card><CardHeader className="pb-1"><CardDescription>Stale (&gt;7d)</CardDescription><CardTitle className="text-2xl">{summary.stale}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">due for re-exercise</CardContent></Card>
        <Card><CardHeader className="pb-1"><CardDescription>With open finding</CardDescription><CardTitle className="text-2xl">{summary.open}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">combinations</CardContent></Card>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={persona} onChange={setPersona} options={personas} label="persona" />
        <Select value={status} onChange={setStatus} options={['ALL', 'NEVER_TESTED', 'STALE', 'RECENT']} label="status" />
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length}/{rows.length} combinations</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Charter</TableHead><TableHead>Persona</TableHead><TableHead>Behavior</TableHead><TableHead>Feature area</TableHead><TableHead>Coverage</TableHead><TableHead>Runs</TableHead><TableHead>Last exercised</TableHead><TableHead>Last outcome</TableHead><TableHead>Verifier</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={`${r.charter_id}|${r.persona}|${r.behavior_style}`}>
                <TableCell className="font-mono text-xs">{r.charter_id}</TableCell>
                <TableCell className="text-xs">{r.persona}</TableCell>
                <TableCell className="text-xs">{r.behavior_style}</TableCell>
                <TableCell className="text-xs">{r.feature_area ?? '—'}</TableCell>
                <TableCell><CovBadge status={r._status} openFinding={(r.open_finding_count ?? 0) > 0} /></TableCell>
                <TableCell className="text-xs">{r.run_count}</TableCell>
                <TableCell className="text-xs">{r.last_run_at ? new Date(r.last_run_at).toLocaleString() : 'never'}</TableCell>
                <TableCell className="text-xs">{r.last_outcome ?? '—'}</TableCell>
                <TableCell className="text-xs">{r.last_verifier_outcome ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function RunsTab() {
  const runs = useQaRuns();
  const [engine, setEngine] = useState('ALL');
  const [result, setResult] = useState('ALL');
  const [cleanup, setCleanup] = useState('ALL');
  const [q, setQ] = useState('');
  // Default: collapse each logical run to its authoritative (latest) attempt.
  // Toggle to reveal every historical attempt — none are ever deleted.
  const [showAllAttempts, setShowAllAttempts] = useState(false);

  // Count attempts per logical run so the UI can flag multi-attempt runs.
  const attemptsByLogical = useMemo(() => attemptCounts(runs.data ?? []), [runs.data]);

  const engines = useMemo(() => ['ALL', ...Array.from(new Set((runs.data ?? []).map((r) => r.engine)))], [runs.data]);
  const filtered = useMemo(() => collapseAttempts(runs.data ?? [], showAllAttempts).filter((r) =>
    (engine === 'ALL' || r.engine === engine) &&
    (result === 'ALL' || normResult(r.result) === result) &&
    (cleanup === 'ALL' || (r.cleanup_status ?? '').toUpperCase() === cleanup) &&
    (!q || r.run_id.toLowerCase().includes(q.toLowerCase()) || (r.logical_run_id ?? '').toLowerCase().includes(q.toLowerCase()) || (r.fingerprint ?? '').toLowerCase().includes(q.toLowerCase()) || (r.seed ?? '').includes(q))
  ), [runs.data, engine, result, cleanup, q, showAllAttempts]);

  if (runs.isLoading) return <Loading />;
  if (runs.error) return <ErrorState e={runs.error} />;
  if (!runs.data?.length) return <Empty what="run" />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Search run id / fingerprint / seed…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 max-w-xs" aria-label="Search runs" />
        <Select value={engine} onChange={setEngine} options={engines} label="engine" />
        <Select value={result} onChange={setResult} options={RESULT_FILTER_OPTIONS} label="result" />
        <Select value={cleanup} onChange={setCleanup} options={['ALL', 'CLEAN', 'DIRTY']} label="cleanup" />
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={showAllAttempts} onChange={(e) => setShowAllAttempts(e.target.checked)} aria-label="Show all attempts" />
          Show all attempts
        </label>
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length}/{runs.data.length} runs{showAllAttempts ? '' : ' (latest attempt per logical run)'}</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Run</TableHead><TableHead>Attempt</TableHead><TableHead>Engine</TableHead><TableHead>Result</TableHead><TableHead>Cleanup</TableHead><TableHead>Seed</TableHead><TableHead>Finished</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((r) => {
              const logical = r.logical_run_id ?? r.run_id;
              const total = attemptsByLogical.get(logical) ?? 1;
              return (
              <TableRow key={r.run_id}>
                <TableCell className="font-mono text-xs"><Link to={`/admin/quality/runs/${r.run_id}`} className="text-primary hover:underline">{r.run_id}</Link></TableCell>
                <TableCell className="text-xs whitespace-nowrap">
                  {r.attempt_number != null ? `#${r.attempt_number}${total > 1 ? ` of ${total}` : ''}` : '—'}
                  {r.is_authoritative === false && <span className="ml-1 text-muted-foreground/60">(superseded)</span>}
                  {r.is_authoritative === true && total > 1 && <span className="ml-1 text-emerald-700">(latest)</span>}
                </TableCell>
                <TableCell className="capitalize">{r.engine}</TableCell>
                <TableCell><ResultBadge result={normResult(r.result)} /></TableCell>
                <TableCell className="text-xs">{r.cleanup_status ?? 'n/a'}{r.residue != null && ` · residue ${r.residue}`}</TableCell>
                <TableCell className="text-xs">{r.seed || 'n/a'}</TableCell>
                <TableCell className="text-xs">{r.finished_at ? new Date(r.finished_at).toLocaleString() : 'n/a'}</TableCell>
                <TableCell><Link to={`/admin/quality/runs/${r.run_id}`}><Button variant="ghost" size="sm">Detail <ExternalLink className="h-3 w-3 ml-1" /></Button></Link></TableCell>
              </TableRow>
            ); })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Findings (filter + search) */

function FindingsTab() {
  const findings = useQaFindings();
  const [severity, setSeverity] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [engine, setEngine] = useState('ALL');
  const [q, setQ] = useState('');

  const severities = useMemo(() => ['ALL', ...Array.from(new Set((findings.data ?? []).map((f) => f.severity).filter(Boolean) as string[]))], [findings.data]);
  const engines = useMemo(() => ['ALL', ...Array.from(new Set((findings.data ?? []).map((f) => f.engine).filter(Boolean) as string[]))], [findings.data]);
  const filtered = useMemo(() => (findings.data ?? []).filter((f) =>
    (severity === 'ALL' || f.severity === severity) &&
    (engine === 'ALL' || f.engine === engine) &&
    (status === 'ALL' || (status === 'OPEN' ? !/FIXED|VERIFIED|CLOSED/i.test(f.status ?? '') : /FIXED|VERIFIED|CLOSED/i.test(f.status ?? ''))) &&
    (!q || (f.title ?? '').toLowerCase().includes(q.toLowerCase()) || f.finding_id.toLowerCase().includes(q.toLowerCase()) || (f.root_cause ?? '').toLowerCase().includes(q.toLowerCase()))
  ), [findings.data, severity, status, engine, q]);

  if (findings.isLoading) return <Loading />;
  if (findings.error) return <ErrorState e={findings.error} />;
  if (!findings.data?.length) return <Empty what="finding" />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Search findings…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 max-w-xs" aria-label="Search findings" />
        <Select value={severity} onChange={setSeverity} options={severities} label="severity" />
        <Select value={engine} onChange={setEngine} options={engines} label="engine" />
        <Select value={status} onChange={setStatus} options={['ALL', 'OPEN', 'CLOSED']} label="status" />
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length}/{findings.data.length}</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Finding</TableHead><TableHead>Engine</TableHead><TableHead>Severity</TableHead><TableHead>Classification</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((f) => (
              <TableRow key={`${f.source}:${f.finding_id}`}>
                <TableCell className="max-w-md"><div className="font-medium text-sm">{f.title ?? f.finding_id}</div>{f.root_cause && <div className="text-xs text-muted-foreground">{f.root_cause}</div>}</TableCell>
                <TableCell className="capitalize text-xs">{f.engine ?? '—'}</TableCell>
                <TableCell className="text-xs">{f.severity ?? '—'}</TableCell>
                <TableCell className="text-xs">{f.classification ?? '—'}</TableCell>
                <TableCell><Badge variant="outline" className={/FIXED|VERIFIED|CLOSED/i.test(f.status ?? '') ? 'border-emerald-300 text-emerald-700' : 'border-amber-400 text-amber-700'}>{f.status ?? 'OPEN'}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Audit debt + Mutations */

function AuditDebtTab() {
  const audit = useQaAuditDebt();
  if (audit.isLoading) return <Loading />;
  if (audit.error) return <ErrorState e={audit.error} />;
  if (!audit.data?.length) return <Empty what="audit-debt" />;
  return (
    <div className="overflow-x-auto">
      <p className="text-sm text-muted-foreground mb-3">Deferred / not-executed scope that must be closed before higher trust levels. These are explicit gaps — not passes.</p>
      <Table>
        <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Engine</TableHead><TableHead>Category</TableHead><TableHead>Blocks</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {audit.data.map((a) => (
            <TableRow key={a.item}>
              <TableCell className="text-sm">{a.item}</TableCell>
              <TableCell className="capitalize text-xs">{a.engine ?? '—'}</TableCell>
              <TableCell className="text-xs">{a.category ?? '—'}</TableCell>
              <TableCell className="text-xs">{a.blocks_level ?? '—'}</TableCell>
              <TableCell><Badge variant="outline" className="border-slate-300 text-slate-600">{a.status}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MutationsTab() {
  const mutations = useQaMutations();
  if (mutations.isLoading) return <Loading />;
  if (mutations.error) return <ErrorState e={mutations.error} />;
  if (!mutations.data?.length) return <Empty what="mutation" />;
  const killed = mutations.data.filter((m) => m.killed).length;
  const total = mutations.data.length;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-1"><CardDescription>Mutation kill score (detected injected defects)</CardDescription><CardTitle className="text-2xl">{killed}/{total} · {((killed / total) * 100).toFixed(0)}%</CardTitle></CardHeader>
        <CardContent className="text-xs text-muted-foreground">A surviving mutation means the QA suite failed to detect an injected defect. Score reflects detector strength, not product correctness.</CardContent>
      </Card>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Mutation</TableHead><TableHead>Category</TableHead><TableHead>Outcome</TableHead><TableHead>Run</TableHead></TableRow></TableHeader>
          <TableBody>
            {mutations.data.map((m) => (
              <TableRow key={`${m.mutation_id}:${m.run_id}`}>
                <TableCell className="text-sm">{m.mutation_id}</TableCell>
                <TableCell className="text-xs">{m.category ?? '—'}</TableCell>
                <TableCell>{m.killed ? <ResultBadge result="PASS" /> : <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700"><XCircle className="h-3.5 w-3.5" /> Survived (undetected)</span>}</TableCell>
                <TableCell className="font-mono text-xs">{m.run_id ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Worker Health (Phase 4 unattended worker) */

// A heartbeat older than this without a STOPPED state is treated as unhealthy
// (the worker likely died without a clean shutdown).
const WORKER_STALE_MS = 10 * 60 * 1000;
const WORKER_STATE_STYLES: Record<string, string> = {
  IDLE: 'border-emerald-300 text-emerald-700', RUNNING: 'border-blue-300 text-blue-700',
  VERIFYING: 'border-blue-300 text-blue-700', PREFLIGHT: 'border-slate-300 text-slate-600',
  CLEANING: 'border-amber-400 text-amber-700', DEGRADED: 'border-amber-500 text-amber-800',
  STOPPED: 'border-red-400 text-red-700',
};

function WorkerHealthTab() {
  const workers = useQaWorkerHeartbeat();
  if (workers.isLoading) return <Loading />;
  if (workers.error) return <ErrorState e={workers.error} />;
  if (!workers.data?.length) return <Empty what="worker heartbeat" />;

  const now = Date.now();
  const rows = workers.data.map((w) => {
    const beatMs = w.last_heartbeat ? Date.parse(w.last_heartbeat) : 0;
    const ageMs = beatMs ? now - beatMs : Infinity;
    const state = (w.current_state ?? 'UNKNOWN').toUpperCase();
    // Fresh IDLE/STOPPED is healthy at rest; anything else that has gone quiet is unhealthy.
    const fresh = ageMs <= WORKER_STALE_MS;
    const atRest = state === 'IDLE' || state === 'STOPPED';
    const healthy = state !== 'DEGRADED' && (fresh || atRest);
    return { w, ageMs, state, healthy };
  });

  return (
    <div className="space-y-4">
      <Alert className="border-slate-300">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Unattended worker health — sanitized telemetry only</AlertTitle>
        <AlertDescription className="text-sm">
          Published by the autonomous worker itself via the signed QCC ingest path. Hostname is hashed; no secrets, tokens, or filesystem
          paths are ever included. A stale heartbeat (&gt;10 min) in a non-resting state indicates the worker died without a clean shutdown.
        </AlertDescription>
      </Alert>

      <div className="grid gap-3 md:grid-cols-2">
        {rows.map(({ w, ageMs, state, healthy }) => (
          <Card key={w.worker_id}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between gap-2">
                <span className="font-mono">{w.worker_id}</span>
                <span className="inline-flex gap-1">
                  <Badge variant="outline" className={WORKER_STATE_STYLES[state] ?? 'border-slate-300 text-slate-600'}>{state}</Badge>
                  <Badge variant="outline" className={healthy ? 'border-emerald-300 text-emerald-700' : 'border-red-400 text-red-700'}>{healthy ? 'HEALTHY' : 'UNHEALTHY'}</Badge>
                </span>
              </CardTitle>
              <CardDescription className="text-xs">
                worker {w.worker_version ?? '?'} @ <code>{w.worker_commit ?? 'unknown'}</code> · host <code>{w.hostname_hash ?? '—'}</code>
              </CardDescription>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground space-y-1">
              <div>Last heartbeat: {w.last_heartbeat ? new Date(w.last_heartbeat).toLocaleString() : 'never'} {Number.isFinite(ageMs) && <span className={ageMs > WORKER_STALE_MS ? 'text-amber-700' : ''}>({Math.round(ageMs / 1000)}s ago)</span>}</div>
              <div>Current run: <span className="text-foreground">{w.current_run_id ?? '—'}</span> · Last completed: <span className="text-foreground">{w.last_completed_run ?? '—'}</span></div>
              <div>Pending outbox: <span className={(w.pending_outbox_count ?? 0) > 0 ? 'text-amber-700 font-medium' : 'text-foreground'}>{w.pending_outbox_count ?? 0}</span>
                {' · '}Consecutive infra failures: <span className={(w.consecutive_infra_failures ?? 0) > 0 ? 'text-amber-700 font-medium' : 'text-foreground'}>{w.consecutive_infra_failures ?? 0}</span></div>
            </CardContent>
          </Card>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">States: IDLE (at rest) · PREFLIGHT · RUNNING · VERIFYING · CLEANING · DEGRADED (recoverable infra failure) · STOPPED (halted on a stop condition). Heartbeat refreshes every 30s.</p>
    </div>
  );
}

/* ------------------------------------------------------------------ shell */

export default function QualityCommandCenter() {
  const navigate = useNavigate();
  const { data: isAdmin, isLoading } = useIsInternalAdmin();

  if (isLoading) return <Loading />;
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-lg py-24 text-center">
        <ShieldCheck className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-4 text-lg font-semibold">403 — Access denied</h1>
        <p className="mt-1 text-sm text-muted-foreground">The Quality Command Center is restricted to platform administrators.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      <div className="mb-4 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2"><Activity className="h-5 w-5" /> Quality Command Center</h1>
          <p className="text-sm text-muted-foreground">Read-only QE Platform V2 evidence · admin-only · QE artifacts remain the source of truth</p>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="readiness">Readiness Matrix</TabsTrigger>
          <TabsTrigger value="runs">Runs</TabsTrigger>
          <TabsTrigger value="coverage">Coverage</TabsTrigger>
          <TabsTrigger value="worker">Worker Health</TabsTrigger>
          <TabsTrigger value="findings">Findings</TabsTrigger>
          <TabsTrigger value="audit">Audit Debt</TabsTrigger>
          <TabsTrigger value="mutations">Mutations</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="soak">Soak</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="simulation">Simulation</TabsTrigger>
          <TabsTrigger value="artifacts">Artifact Health</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>
        <div className="mt-4">
          <TabsContent value="overview"><OverviewTab /></TabsContent>
          <TabsContent value="readiness"><ReadinessMatrixTab /></TabsContent>
          <TabsContent value="runs"><RunsTab /></TabsContent>
          <TabsContent value="coverage"><CoverageTab /></TabsContent>
          <TabsContent value="worker"><WorkerHealthTab /></TabsContent>
          <TabsContent value="findings"><FindingsTab /></TabsContent>
          <TabsContent value="audit"><AuditDebtTab /></TabsContent>
          <TabsContent value="mutations"><MutationsTab /></TabsContent>
          <TabsContent value="performance"><PerformanceTab /></TabsContent>
          <TabsContent value="soak"><SoakTab /></TabsContent>
          <TabsContent value="security"><SecurityMatrixTab /></TabsContent>
          <TabsContent value="simulation"><SimulationTab /></TabsContent>
          <TabsContent value="artifacts"><ArtifactHealthTab /></TabsContent>
          <TabsContent value="trends"><TrendsTab /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
